<template>
  <BaseCard class="flex h-125 flex-col gap-4 p-4 md:p-6">
    <!-- Header -->
    <div>
      <TextSubtitle>Onbalansprijzen</TextSubtitle>
    </div>
    <!-- Stat row -->
    <LoadingWrapper :error="loadingFailed" :loading="loading">
      <div class="flex items-start justify-between">
        <!-- Current feed-in price -->
        <StatComponent
          v-if="showCurrentPrices"
          label="Huidige prijs opregelen"
          :value="current.feedInPrice"
          :precision="2"
          format="€ _"
          class="flex-1"
          small
        >
          <template #icon>
            <BaseIcon name="arrow-up" class="fill-finance" />
          </template>
        </StatComponent>
        <!-- Current take-off price -->
        <StatComponent
          v-if="showCurrentPrices"
          label="Huidige prijs afregelen"
          :value="current.takeOffPrice"
          :precision="2"
          format="€ _"
          class="flex-1"
          small
        >
          <template #icon>
            <BaseIcon name="arrow-down" class="fill-secondary" />
          </template>
        </StatComponent>
      </div>
      <!-- Chart -->
      <TimeseriesChart
        chart-type="step"
        :series="series"
        :axes="axes"
        :data="chartData"
        :x-limits="[dateRange[0], endOfDay(dateRange[1])]"
        :annotation-data="annotationData.length ? annotationData : null"
      />
    </LoadingWrapper>
  </BaseCard>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import {
  endOfDay,
  roundToNearestMinutes,
  startOfToday,
  startOfTomorrow,
} from "date-fns"
import { debounce, DEBOUNCE_DELAY } from "@/helpers/debounce.js"
import useMarketPricesStore from "@/stores/repower/marketPricesStore.js"
import useNotificationStore from "@/stores/notificationStore.js"
import {
  BaseCard,
  BaseIcon,
  TextSubtitle,
  LoadingWrapper,
  TimeseriesChart,
} from "@repowerednl/ui-component-library"
import StatComponent from "@/components/information/StatComponent.vue"

const props = defineProps({
  dateRange: {
    type: Array,
    required: true,
  },
})

const TIME_INTERVAL = 15
const marketPricesStore = useMarketPricesStore()
const notificationStore = useNotificationStore()
const chartData = ref({})
const annotationData = ref([])
const loading = ref(true)
const loadingFailed = ref(false)

const forecastKeyMap = {
  take_from_system: "take_from_system_forecast",
  feed_into_system: "feed_into_system_forecast",
}

const series = {
  feed_into_system: {
    label: "Opregelen",
    axis: "finance",
    color: "finance",
  },
  feed_into_system_forecast: {
    label: "Opregelen (voorspeld)",
    axis: "finance",
    secondary: true,
    color: "finance",
  },
  take_from_system: {
    label: "Afregelen",
    axis: "finance",
    color: "secondary",
  },
  take_from_system_forecast: {
    label: "Afregelen (voorspeld)",
    axis: "finance",
    secondary: true,
    color: "secondary",
  },
}

const axes = {
  finance: {
    title: "Prijs per MWh",
    format: "€ _",
    precision: 0,
  },
}

const current = computed(() => {
  const { data } = marketPricesStore.imbalancePrices
  const lastValue = Object.values(data)
    .reverse()
    .find((v) => v.stable !== null)

  return {
    feedInPrice: lastValue?.["feed_into_system"] ?? NaN,
    takeOffPrice: lastValue?.["take_from_system"] ?? NaN,
  }
})

const showCurrentPrices = computed(
  () =>
    props.dateRange[0] < startOfTomorrow() &&
    props.dateRange[1] >= startOfToday(),
)

const loadData = debounce((range) => {
  marketPricesStore.loadImbalancePrices(range, onNewPrices, onLoadPricesFailed)
}, DEBOUNCE_DELAY)

function onNewPrices(data) {
  const splitData = {}
  for (const [timestamp, values] of Object.entries(data)) {
    splitData[timestamp] = {}
    if (!values.stable) {
      for (const [key, forecastKey] of Object.entries(forecastKeyMap)) {
        splitData[timestamp][forecastKey] = values[key]
      }
    } else {
      splitData[timestamp] = values
    }
  }
  chartData.value = splitData
  onNewAnnotations(data)
  loading.value = false
}

function onLoadPricesFailed(error) {
  loadingFailed.value = true
  notificationStore.pushError(
    "Fout bij het ophalen van onbalans prijzen",
    `De onbalans prijzen konden niet worden opgehaald. Probeer het later opnieuw. (code: ${error.code})`,
    "load-imbalance-market-error",
  )
}

/**
 * Returns timestamps where `regulation_state === 2` for use as annotations in
 * the imbalance chart. Groups consecutive timestamps into annotation ranges.
 *
 * Each grouped range's final dateTimeStart is extended by one interval (e.g.,
 * 15 min) to be used as dateTimeStop *except* when the last entry is ongoing,
 * meaning the ongoing entry annotation ends on its dateTimeStart
 *
 * The grouped data is assigned to `annotationData` for visual display in the
 * chart.
 *
 * @param {Object} data - The imbalance market data with timestamp keys and
 * regulation state info.
 */
function onNewAnnotations(data) {
  const entries = Object.entries(data)
    .filter(([, entry]) => entry.regulation_state === 2)
    .sort(([a], [b]) => new Date(a) - new Date(b))

  const MS_INTERVAL = TIME_INTERVAL * 60 * 1000
  const grouped = []
  let rangeStart = null
  let prevTime = null

  // Determine the current time floored to the nearest TIME_INTERVAL
  // This is used to determine if the last entry is ongoing
  const now = new Date()
  const nowFloored = roundToNearestMinutes(now, {
    nearestTo: TIME_INTERVAL,
    roundingMethod: "floor",
  })

  for (const [timestamp] of entries) {
    const currentTime = new Date(timestamp)

    // Start a new group
    if (!rangeStart) {
      rangeStart = currentTime
    }

    // If timestamps are not consecutive, close the previous group
    if (prevTime && currentTime - prevTime !== MS_INTERVAL) {
      const rangeEnd = new Date(prevTime.getTime() + MS_INTERVAL)

      grouped.push({
        datetimeStart: rangeStart.toISOString(),
        datetimeStop: rangeEnd.toISOString(),
      })

      // Start new range
      rangeStart = currentTime
    }

    prevTime = currentTime
  }

  // Push the final group, extending the end time
  if (rangeStart && prevTime) {
    const lastEnd = new Date(prevTime.getTime() + MS_INTERVAL)
    const isOngoing = prevTime.getTime() === nowFloored.getTime()

    grouped.push({
      datetimeStart: rangeStart.toISOString(),
      // If the last entry is ongoing don't extend the end time
      datetimeStop: isOngoing ? prevTime.toISOString() : lastEnd.toISOString(),
    })
  }

  annotationData.value = grouped.length
    ? [
        {
          label: "Regeltoestand 2",
          color: "danger",
          data: grouped,
        },
      ]
    : []
}

watch(
  () => props.dateRange,
  (newRange) => {
    loading.value = true
    loadData(newRange)
  },

  { immediate: true },
)
</script>
